import { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';

import { Breakpoints } from 'hooks/Screen/useScreenSize';
import { ObjectItem } from 'api/clients/types';
import EmailNotificationBTN from 'components/ClientTable/EmailNotificationBTB';
import { useAddTechnicianModalStore } from 'components/ClientTable/Modals/AddTechnicanModal/AddTechnicianModalStore';
import { useDeleteTechnicianModalStore } from 'components/ClientTable/Modals/DeleteTechnicianModal/DeleteTechnicianModalStore';

interface UseGetObjectsColumnsProps {
  setSelectedObject: (obj: ObjectItem | undefined) => void;
  setIsDeleteModalOpen: (isOpen: boolean) => void;
  setIsManageObjectModalOpen: (isOpen: boolean) => void;
}

export const useGetObjectsColumns = ({
  setSelectedObject,
  setIsDeleteModalOpen,
  setIsManageObjectModalOpen,
}: UseGetObjectsColumnsProps): TableColumn<ObjectItem>[] => {
  const { t } = useTranslation();
  const { open: openAddTechnicianModal } = useAddTechnicianModalStore();
  const { open: openDeleteTechnicianModal } = useDeleteTechnicianModalStore();

  return [
    {
      name: <div className="fs-15">{t('ClientTable.titles.object')}</div>,
      selector: row => row.name,
      cell: row => (
        <a
          href={`/r/object/traps/${row.slug}`}
          type="button"
          className={`m-0 p-0 ${!row.is_activity ? 'bg-[#000]' : ''}`}
        >
          <span className="table-title fs-15">{row.name}</span>
        </a>
      ),
      grow: 2,
      minWidth: '150px',
    },
    {
      name: <div className="fs-15">{t('ClientTable.titles.city')}</div>,
      selector: row => row.address,
      hide: Breakpoints.Tablet,
      cell: row => (
        <div style={{ textOverflow: 'ellipsis' }} className="text-nowrap overflow-hidden " title={row.address}>
          {row.address}
        </div>
      ),
      minWidth: '150px',
    },
    {
      name: <div className="fs-15">{t('ClientTable.titles.manager')}</div>,
      selector: row => (row.manager ? row.manager.first_name + ' ' + row.manager.last_name : '-'),
      cell: row => (
        <div style={{ textOverflow: 'ellipsis' }} className="text-nowrap overflow-hidden">
          {row.manager ? row.manager.first_name + ' ' + row.manager.last_name : '-'}
        </div>
      ),
      hide: Breakpoints.Desktop,
      minWidth: '130px',
    },
    {
      name: <div className="fs-15">{t('ClientTable.titles.technician')}</div>,
      selector: row => row.technicians?.length,
      cell: row => (
        <div className="d-flex align-items-center justify-content-between">
          <div className="avatar-group">
            {row.technicians.map(siteTech => (
              <div
                className={`avatar-group-item avatar-wrapper ${
                  siteTech.is_lead && 'border-warning'
                } d-flex align-items-center`}
                key={siteTech.technician.id}
              >
                <img
                  src={siteTech.technician.avatar}
                  alt={siteTech.technician.last_name}
                  title={`${siteTech.technician.last_name} ${siteTech.technician.first_name}`}
                  className="rounded-circle avatar-xs"
                />

                <i
                  className="ri-delete-bin-5-fill avatar-delete-icon"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    openDeleteTechnicianModal(row.slug, siteTech, false, true);
                  }}
                />
              </div>
            ))}
          </div>
          <div>
            <Button
              onClick={() => {
                openAddTechnicianModal(row.slug);
              }}
              type="button"
              className="btn btn-sm btn-rounded rounded-5 btn-soft-success custom-toggle waves-light mx-2"
              title="Add technician"
            >
              <span className="icon-on fs-5">
                <i className="ri-add-circle-fill align-bottom" />
              </span>
            </Button>
          </div>
        </div>
      ),
      style: {
        justifyContent: 'end',
      },
      hide: Breakpoints.Mobile,
      minWidth: '180px',
    },
    {
      name: <div className="fs-15">{t('ClientTable.titles.amount-traps')}</div>,
      selector: row => row.mct_count + row.smst_count + row.srst_count + row.srbm_count,
      cell: row => <div>{row.mct_count + row.smst_count + row.srst_count + row.srbm_count}</div>,
      hide: Breakpoints.Desktop,
      width: '110px',
      center: true,
    },
    {
      name: <div className="fs-15">{t('ClientTable.titles.notifications')}</div>,
      selector: row => row.mct_count + row.smst_count + row.srst_count + row.srbm_count,
      cell: row => (
        <EmailNotificationBTN
          objectSlug={String(row.slug)}
          mct_receive_messages={row.email_messagess_send.mct_receive_messages}
          srbm_receive_messages={row.email_messagess_send.srbm_receive_messages}
          smst_receive_messages={row.email_messagess_send.smst_receive_messages}
          srst_receive_messages={row.email_messagess_send.srst_receive_messages}
          mct_count={row.mct_count}
          smst_count={row.smst_count}
          srst_count={row.srst_count}
          srbm_count={row.srbm_count}
        />
      ),
      width: '145px',
      center: true,
    },
    {
      name: <div className="fs-15">{t('ClientTable.titles.actions')}</div>,
      selector: row => row.slug,
      cell: row => (
        <div className="btn-group mt-4 mt-sm-0" role="group">
          <button
            type="button"
            className="btn btn-sm btn-ghost-primary btn-icon"
            onClick={() => {
              setSelectedObject(row);
              setIsManageObjectModalOpen(true);
            }}
          >
            <i className="ri-pencil-fill" />
          </button>
          <button
            onClick={() => {
              setIsDeleteModalOpen(true);
              setSelectedObject(row);
            }}
            type="button"
            className="btn btn-sm btn-ghost-primary btn-icon"
          >
            <span className="remove-item-btn">
              <i className="ri-delete-bin-6-fill" />
            </span>
          </button>
        </div>
      ),
      hide: Breakpoints.Mobile,
      width: '100px',
      center: true,
    },
  ];
};
